import { formatDate, formatTime, toBerlinDateKey } from '@brimo/shared';

/**
 * Small date helpers of the contacts module: calendar days are `YYYY-MM-DD` keys in Europe/Berlin
 * (the API's `dueOn`), the UI shows them as `dd.mm.yyyy`; the calendar popover works on month grids.
 * There is no date library (FRONTEND-COMMON.md) — everything here is arithmetic on day keys.
 */

export const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;

export function todayKey(): string {
  return toBerlinDateKey(new Date());
}

/** `2026-09-19` → `19.09.2026`; anything else is returned unchanged. */
export function formatDayKey(key: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : key;
}

/** `19.09.2026` (or `19.9.26`) → `2026-09-19`; null when the text is not a date. */
export function parseGermanDate(text: string): string | null {
  const match = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})\s*$/.exec(text);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month - 1)) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

export function partsOf(key: string): { year: number; month: number; day: number } {
  const [year = 0, month = 1, day = 1] = key.split('-').map(Number);
  return { year, month: month - 1, day };
}

export function keyOf(year: number, monthIndex: number, day: number): string {
  const date = new Date(Date.UTC(year, monthIndex, day));
  return date.toISOString().slice(0, 10);
}

export function addDays(key: string, days: number): string {
  const { year, month, day } = partsOf(key);
  return keyOf(year, month, day + days);
}

/** ISO weekday index with Monday = 0. */
export function weekdayIndex(key: string): number {
  const { year, month, day } = partsOf(key);
  return (new Date(Date.UTC(year, month, day)).getUTCDay() + 6) % 7;
}

export interface CalendarCell {
  key: string;
  day: number;
  inMonth: boolean;
}

/** The cells of a month grid (Monday-first, whole weeks). */
export function monthGrid(year: number, monthIndex: number): CalendarCell[] {
  const first = keyOf(year, monthIndex, 1);
  const lead = weekdayIndex(first);
  const start = addDays(first, -lead);
  const count = daysInMonth(year, monthIndex);
  const rows = Math.ceil((lead + count) / 7);
  const cells: CalendarCell[] = [];
  for (let index = 0; index < rows * 7; index += 1) {
    const key = addDays(start, index);
    const parts = partsOf(key);
    cells.push({ key, day: parts.day, inMonth: parts.month === monthIndex && parts.year === year });
  }
  return cells;
}

export function monthTitle(year: number, monthIndex: number): string {
  return `${MONTHS[monthIndex] ?? ''} ${year}`;
}

/** "Heute" / "Morgen" / "Gestern" / "26.09.2026" for a point in time (Berlin day). */
export function relativeDay(iso: string, now = new Date()): string {
  const key = toBerlinDateKey(iso);
  const today = toBerlinDateKey(now);
  if (key === today) return 'Heute';
  if (key === addDays(today, 1)) return 'Morgen';
  if (key === addDays(today, -1)) return 'Gestern';
  return formatDate(iso);
}

/** "Heute, 09:00" — day and time of an instant. */
export function relativeDayTime(iso: string, now = new Date()): string {
  return `${relativeDay(iso, now)}, ${formatTime(iso)}`;
}

/** Whole days between two day keys (`to` − `from`). */
export function daysBetween(from: string, to: string): number {
  const a = partsOf(from);
  const b = partsOf(to);
  return Math.round((Date.UTC(b.year, b.month, b.day) - Date.UTC(a.year, a.month, a.day)) / 86_400_000);
}

/** "Mi 16.09." — weekday and day/month of a slot. */
export function slotDay(iso: string): string {
  const key = toBerlinDateKey(iso);
  const { day, month } = partsOf(key);
  const weekday = WEEKDAYS[weekdayIndex(key)] ?? '';
  return `${weekday} ${String(day).padStart(2, '0')}.${String(month + 1).padStart(2, '0')}.`;
}

/** "16.09." — the short day of the timeline and list texts. */
export function shortDay(iso: string): string {
  const key = toBerlinDateKey(iso);
  const { day, month } = partsOf(key);
  return `${String(day).padStart(2, '0')}.${String(month + 1).padStart(2, '0')}.`;
}

/** "n Tag" / "n Tage". */
export function daysText(days: number): string {
  return `${days} ${days === 1 ? 'Tag' : 'Tage'}`;
}

/** An ISO instant for a Berlin day key and `HH:MM` (the API converts with the same zone). */
export function berlinInstant(key: string, time: string): string {
  const { year, month, day } = partsOf(key);
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  // Europe/Berlin offset for that day (CET/CEST) via the difference between the UTC guess and its local rendering.
  const guess = new Date(Date.UTC(year, month, day, hour, minute));
  const rendered = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(guess);
  const get = (type: string) => Number(rendered.find((part) => part.type === type)?.value ?? '0');
  const local = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  const offset = local - guess.getTime();
  return new Date(guess.getTime() - offset).toISOString();
}
